import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ServiceCatalogEntry {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  category: string;
  unit_label: string | null;
  sort_order: number;
  is_active: boolean;
}

export const SERVICE_CATEGORIES = [
  { value: 'meal', label: 'Meal' },
  { value: 'beverage', label: 'Beverage' },
  { value: 'transit', label: 'Transit' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'other', label: 'Other' },
];

const FALLBACK_DISPLAY: Record<string, string> = {
  breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner',
  milk: 'Milk', juice: 'Juice', metro: 'Metro',
  laundry: 'Laundry', meal: 'Meal',
};

/** Convert a raw service_type string into a normalized slug. */
export function slugifyServiceType(raw: string | null | undefined): string {
  const lower = (raw || '').toLowerCase().trim();
  if (!lower) return 'other';
  if (lower === 'breakfast') return 'breakfast';
  if (lower === 'lunch') return 'lunch';
  if (lower === 'dinner') return 'dinner';
  if (lower === 'milk') return 'milk';
  if (lower === 'juice') return 'juice';
  if (lower.includes('metro') || lower.includes('transit')) return 'metro';
  if (lower.includes('laundry') || lower.includes('wash')) return 'laundry';
  if (lower.includes('beverage') || lower.includes('drink')) return 'juice';
  if (lower.includes('meal') || lower.includes('food')) return 'meal';
  return lower.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'other';
}

export function useServiceCatalog() {
  const queryClient = useQueryClient();

  const { data: catalog = [], isLoading } = useQuery({
    queryKey: ['service-catalog'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('service_catalog')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return (data || []) as ServiceCatalogEntry[];
    },
  });

  const bySlug = new Map<string, ServiceCatalogEntry>();
  catalog.forEach((s) => bySlug.set(s.slug, s));

  const displayName = (rawType: string | null | undefined): string => {
    const slug = slugifyServiceType(rawType);
    const entry = bySlug.get(slug);
    if (entry) return entry.name;
    if (FALLBACK_DISPLAY[slug]) return FALLBACK_DISPLAY[slug];
    return (rawType || 'Other').toString();
  };

  const activeCatalog = catalog.filter((s) => s.is_active);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['service-catalog'] });

  const addService = useMutation({
    mutationFn: async (input: { name: string; category: string; unit_label?: string | null }) => {
      // get org id from existing entry, or fetch
      let orgId = catalog[0]?.organization_id;
      if (!orgId) {
        const { data: userRes } = await supabase.auth.getUser();
        const uid = userRes.user?.id;
        if (!uid) throw new Error('Not signed in');
        const { data: m } = await supabase
          .from('organization_members').select('organization_id').eq('user_id', uid).maybeSingle();
        orgId = m?.organization_id;
      }
      if (!orgId) throw new Error('No organization found');
      const slug = slugifyServiceType(input.name);
      const maxOrder = catalog.reduce((m, c) => Math.max(m, c.sort_order), 0);
      const { error } = await supabase.from('service_catalog').insert({
        organization_id: orgId,
        name: input.name.trim(),
        slug,
        category: input.category,
        unit_label: input.unit_label?.trim() || null,
        sort_order: maxOrder + 10,
        is_active: true,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const updateService = useMutation({
    mutationFn: async (input: { id: string; patch: Partial<Pick<ServiceCatalogEntry, 'name' | 'category' | 'unit_label' | 'is_active' | 'sort_order'>> }) => {
      const { error } = await supabase
        .from('service_catalog')
        .update(input.patch)
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deleteService = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('service_catalog').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return {
    catalog,
    activeCatalog,
    bySlug,
    displayName,
    slugify: slugifyServiceType,
    isLoading,
    addService,
    updateService,
    deleteService,
  };
}
